import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Home, ArrowRight } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(name, email, password);
      nav("/");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="grid-pattern absolute inset-0 opacity-[0.04]" />
        <div className="relative flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-lg bg-white grid place-items-center">
            <Home className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
          </div>
          <div className="font-display font-bold text-xl tracking-tight">RoostHQ</div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
            Start collecting rent<br/>
            <span className="text-slate-400">the calm way.</span>
          </h1>
          <ul className="space-y-3 text-slate-300 text-sm">
            <li>— Monthly rent roll auto-generated per contract</li>
            <li>— Rent increase alerts before you lose money</li>
            <li>— Email reminders to nudge overdue tenants</li>
            <li>— Gross, commission, tax and nett — always visible</li>
          </ul>
        </div>
        <div className="relative text-xs uppercase tracking-widest text-slate-500">Currency: R (ZAR) · Date: MM/DD/YYYY</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Get started</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight mt-1">Create your account</h2>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500">Name</span>
              <input
                required value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                className="mt-1.5 w-full h-11 px-3.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm"
                placeholder="Jane Landlord"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500">Email</span>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                className="mt-1.5 w-full h-11 px-3.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm"
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500">Password</span>
              <input
                type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                className="mt-1.5 w-full h-11 px-3.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 text-sm"
                placeholder="At least 6 characters"
              />
            </label>
          </div>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5" data-testid="register-error">{err}</div>}

          <button
            type="submit" disabled={loading} data-testid="register-submit-btn"
            className="w-full h-11 rounded-lg bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? "Creating…" : "Create account"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-sm text-slate-500">
            Already registered?{" "}
            <Link to="/login" className="text-slate-900 font-medium underline underline-offset-4" data-testid="link-login">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
